import { Box, Typography } from '@mui/material';
import { type SyntheticEvent } from 'react';

import VNRichtextDisplay from '@/components/VNRichtext/VNRichtextDisplay';

export type NotificationBodyFormat = 'html' | 'richtext';

interface NotificationBodyContentProps {
  body?: string | null;
  /** `html` renders in a sandboxed iframe; `richtext` renders markdown to match the editor. */
  format: NotificationBodyFormat;
  /** When true, the HTML iframe auto-sizes to its content height (hub detail pane). */
  bodyOnly?: boolean;
}

/**
 * Renders a notification body in the format it was authored in. HTML bodies are shown in a
 * sandboxed iframe (user-activated links may navigate the top window via `target="_top"`);
 * richtext (markdown) bodies are rendered with the same prose baseline as the `VNRichtext`
 * editor (see the `vn-prose` mixin in `vn-richtext.scss`) so display matches editing.
 */
const NotificationBodyContent = ({ body, format, bodyOnly = false }: NotificationBodyContentProps) => {
  const content = String(body ?? '');
  const hasBody = Boolean(body);

  const handleIframeLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
    if (!bodyOnly) {
      return;
    }
    const iframe = event.currentTarget;
    try {
      const doc = iframe.contentDocument;
      if (doc?.body) {
        iframe.style.height = `${Math.max(doc.body.scrollHeight, 1)}px`;
      }
    } catch {
      // Cross-origin or empty document — leave default height.
    }
  };

  if (!hasBody) {
    return (
      <Box className="notification-body__state">
        <Typography variant="body2" color="text.secondary">
          No body content yet.
        </Typography>
      </Box>
    );
  }

  if (format === 'html') {
    return (
      <iframe
        className="notification-body__iframe"
        title="Notification body"
        srcDoc={content}
        sandbox="allow-top-navigation-by-user-activation"
        onLoad={handleIframeLoad}
      />
    );
  }

  return (
    <div className="notification-body__markdown">
      <VNRichtextDisplay markdown={content} />
    </div>
  );
};

export default NotificationBodyContent;
